import { yupResolver } from '@hookform/resolvers/yup';
import { CircularProgress, Dialog, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { setUpTOTP, updateMFAPreference, verifyTOTPSetup } from 'aws-amplify/auth';
import { type FC, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';
import { TotpSetupForm } from '../../Forms';

interface MfaSetupParams {
  code: string;
}

const mfaSetupSchema = yup.object({ code: yup.string().required('Code is required') });

interface IProps {
  open: boolean;
  setOpen: (val: boolean) => void;
  callbackSuccess?: () => void;
  email?: string;
}

export const MfaSetupDialog: FC<IProps> = ({ open, setOpen, callbackSuccess, email }): JSX.Element => {
  const [loading, setLoading] = useState(false);
  const [setupUri, setSetupUri] = useState('');

  const {
    setError,
    reset,
    handleSubmit,
    register,
    formState: { errors }
  } = useForm<MfaSetupParams>({
    resolver: yupResolver<MfaSetupParams>(mfaSetupSchema),
    mode: 'onSubmit'
  });

  const closeDialog = () => {
    setOpen(false);
    setSetupUri('');
    reset();
  };

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const totpSetupDetails = await setUpTOTP();
        setSetupUri(totpSetupDetails.getSetupUri('Avani Innsights', email).toString());
      } catch {
        closeDialog();
      }
    })();
  }, [open]);

  const onSubmit = async (values: MfaSetupParams) => {
    setLoading(true);
    try {
      await verifyTOTPSetup({ code: values.code });
      await updateMFAPreference({ totp: 'PREFERRED' });
      closeDialog();
      if (callbackSuccess) callbackSuccess();
    } catch (err) {
      const errorMessage =
        typeof err === 'object' && err !== null && 'message' in err
          ? (err as { message: string }).message
          : 'An error occurred';
      setError('root', { type: 'custom', message: errorMessage });
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={() => closeDialog()}>
      <DialogTitle>Enable two-factor authentication</DialogTitle>
      <DialogContent>
        <DialogContentText>Scan the QR code with an authenticator app to enable MFA.</DialogContentText>
        {setupUri ? (
          <TotpSetupForm
            register={register}
            errors={errors}
            handleSubmit={handleSubmit(onSubmit)}
            loading={loading}
            setupUri={setupUri}
          />
        ) : (
          <CircularProgress sx={{ display: 'block', mx: 'auto', my: 4 }} />
        )}
      </DialogContent>
    </Dialog>
  );
};
